/**
 * REEDOS Knowledge Base — landing page at /kb.
 *
 * A modest, useful landing page: shows the sidebar (from KBLayout)
 * plus a welcome panel and a "recent releases" strip. When there is
 * no content yet (Phase A pre-seed) we render a helpful zero-state.
 */
import { Link, useOutletContext } from "react-router-dom";
import { BookOpen, Compass, History } from "lucide-react";
import { useEffect, useState } from "react";
import { kbListVersions } from "@/lib/kb";

export default function KBHome() {
    const { nodes, cfg, productCodeToName } = useOutletContext();
    const [versions, setVersions] = useState([]);

    useEffect(() => {
        kbListVersions(5).then((d) => setVersions(d.items || [])).catch(() => setVersions([]));
    }, []);

    const totalArticles = (nodes || []).reduce((n, x) => n + (x.articles?.length || 0), 0);
    const products = Object.entries(productCodeToName || {});

    return (
        <div className="max-w-3xl">
            <header className="mb-6">
                <h1 className="text-3xl font-semibold text-foreground flex items-center gap-2">
                    <BookOpen className="h-7 w-7 text-primary" />
                    Knowledge Base
                </h1>
                <p className="mt-2 text-muted-foreground">
                    Role-aware documentation for every REEDOS product. Use the
                    sidebar to browse, the search box above to find, or the
                    context-sensitive help (?) icon on any REEDOS page to jump
                    straight to the relevant article.
                </p>
            </header>

            {/* ── Stat strip ───────────────────────────────────── */}
            <div
                className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8"
                data-testid="kb-home-stat-strip"
            >
                <div className="rounded-md border border-border bg-card p-4">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">
                        Articles visible to you
                    </div>
                    <div className="text-2xl font-semibold mt-1">{totalArticles}</div>
                </div>
                <div className="rounded-md border border-border bg-card p-4">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">
                        Products
                    </div>
                    <div className="text-2xl font-semibold mt-1">{products.length}</div>
                </div>
                <div className="rounded-md border border-border bg-card p-4">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">
                        Released versions
                    </div>
                    <div className="text-2xl font-semibold mt-1">{versions.length}</div>
                </div>
            </div>

            {totalArticles === 0 && (
                <div
                    className="rounded-md border border-dashed border-border p-6 bg-muted/40 mb-8"
                    data-testid="kb-home-zero-state"
                >
                    <div className="flex items-start gap-3">
                        <Compass className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                        <div>
                            <div className="font-medium">Content is on its way.</div>
                            <p className="text-sm text-muted-foreground mt-1">
                                The Knowledge Base platform is live but seed
                                articles are being authored by your team. Once
                                published, they will appear in the sidebar and
                                context-sensitive help throughout REEDOS.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Products by name ─────────────────────────────── */}
            <section className="mb-8">
                <h2 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                    Products
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {products.map(([code, name]) => (
                        <Link
                            key={code}
                            to={`/kb?product=${code}`}
                            className="rounded-md border border-border p-3 hover:border-primary hover:bg-muted/40 flex items-center justify-between"
                            data-testid={`kb-home-product-${code}`}
                        >
                            <span className="font-medium">{name}</span>
                            <span className="text-xs text-muted-foreground">
                                {(nodes || []).filter((n) => n.product === code).reduce(
                                    (n, x) => n + (x.articles?.length || 0), 0,
                                )}{" "}
                                articles
                            </span>
                        </Link>
                    ))}
                </div>
            </section>

            {/* ── Recent versions ──────────────────────────────── */}
            {versions.length > 0 && (
                <section>
                    <h2 className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
                        <History className="h-3.5 w-3.5" />
                        Recent releases
                    </h2>
                    <ul className="divide-y divide-border border border-border rounded-md">
                        {versions.slice(0, 5).map((v) => (
                            <li key={v.semver} className="px-3 py-2 text-sm">
                                <span className="font-medium">{v.semver}</span>
                                <span className="text-muted-foreground ml-2">
                                    {v.released_at}
                                </span>
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </div>
    );
}
